import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { Calendar, TrendingUp, AlertCircle, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { format } from "date-fns";
import { toast } from "sonner";
import JSZip from 'jszip';

function formatDose(lb: number | null, mg: number): string {
  if (lb == null || mg <= 0) return '—';
  return (lb / mg).toFixed(2);
}

interface WellStats {
  wellId: number;
  wellName: string;
  totalGallons: number;
  totalMG: number;
  totalInchesUsed: number;
  totalGallonsDelivered: number | null;
  totalLbsCl2: number | null;
  weeklyLbsCl2: { w1: number; w2: number; w3: number; w4: number; w5: number };
  residualLast: number | null;
  residualAvg: number | null;
  missingDaysCount: number;
  missingResidualCount: number;
  missingClCalcCount: number;
}

interface SupervisorStatsResponse {
  wells: WellStats[];
  systemTotals: {
    totalGallons: number;
    totalMG: number;
    totalLbsCl2: number;
    weeklyLbsCl2: { w1: number; w2: number; w3: number; w4: number; w5: number };
    missingDaysCount: number;
    missingResidualCount: number;
    missingClCalcCount: number;
  };
  period: {
    start: string;
    end: string;
  };
}

export default function SupervisorStats() {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  const now = new Date();
  const lastMonth = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const lastMonthNum = now.getMonth() === 0 ? 12 : now.getMonth();

  const [year, setYear] = useState(lastMonth.toString());
  const [month, setMonth] = useState(lastMonthNum.toString());

  const { data: stats, refetch } = trpc.reports.supervisorStats.monthlySummary.useQuery(
    {
      year: parseInt(year),
      month: parseInt(month),
    },
    {
      enabled: !!year && !!month,
    }
  );

  const { data: thresholds } = trpc.reports.supervisorStats.getIndicatorThresholds.useQuery();

  const [generating, setGenerating] = useState(false);

  const allWellsQuery = trpc.reports.monthlyAllWells.useQuery(
    {
      year: parseInt(year),
      month: parseInt(month),
    },
    { enabled: false }
  );

  const dailyDetailQuery = trpc.reports.supervisorStats.stateReportPack.dailyDetail.useQuery(
    {
      year: parseInt(year),
      month: parseInt(month),
    },
    { enabled: false }
  );

  const configSnapshotQuery = trpc.reports.supervisorStats.stateReportPack.chlorineConfigSnapshot.useQuery(
    {
      year: parseInt(year),
      month: parseInt(month),
    },
    { enabled: false }
  );

  const handleLastMonth = () => {
    setYear(lastMonth.toString());
    setMonth(lastMonthNum.toString());
  };

  const handleGenerate = () => {
    refetch();
  };

  const arrayToCSV = (data: any[]): string => {
    if (!data.length) return '';
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => JSON.stringify(row[header] || '')).join(','))
    ].join('\n');
    return csvContent;
  };

  const generateSupervisorStatsCSV = (statsData: any) => {
    const rows = statsData.wells.map((w: any) => ({
      wellName: w.wellName,
      totalGallons: w.totalGallons,
      totalMG: w.totalMG,
      totalInchesUsed: w.totalInchesUsed,
      totalGallonsDelivered: w.totalGallonsDelivered,
      totalLbsCl2: w.totalLbsCl2,
      residualLast: w.residualLast,
      residualAvg: w.residualAvg,
      missingDaysCount: w.missingDaysCount,
      missingResidualCount: w.missingResidualCount,
      missingClCalcCount: w.missingClCalcCount,
    }));
    rows.push({
      wellName: 'SYSTEM TOTALS',
      totalGallons: statsData.systemTotals.totalGallons,
      totalMG: statsData.systemTotals.totalMG,
      totalInchesUsed: '',
      totalGallonsDelivered: '',
      totalLbsCl2: statsData.systemTotals.totalLbsCl2,
      residualLast: '',
      residualAvg: '',
      missingDaysCount: statsData.systemTotals.missingDaysCount,
      missingResidualCount: statsData.systemTotals.missingResidualCount,
      missingClCalcCount: statsData.systemTotals.missingClCalcCount,
    });
    return arrayToCSV(rows);
  };

  const generateAllWellsCSV = (data: any[]) => arrayToCSV(data || []);

  const generateDailyDetailCSV = (data: any[]) => arrayToCSV(data || []);

  const generateConfigSnapshotCSV = (data: any[]) => arrayToCSV(data || []);

  const handleStateReportPack = async () => {
    if (!stats) {
      toast.error('Load stats first');
      return;
    }
    setGenerating(true);
    try {
      await Promise.all([
        allWellsQuery.refetch(),
        dailyDetailQuery.refetch(),
        configSnapshotQuery.refetch(),
      ]);

      const zip = new JSZip();

      const period = `${year}-${month.padStart(2, '0')}`;
      const readmeText = [
        `GeneratedAt: ${new Date().toISOString()}`,
        `Period: ${period}`,
        '',
        'Included files:',
        `- supervisor-stats-${period}.csv: Monthly supervisor summary aggregates per well + system totals`,
        `- all-wells-production-${period}.csv: Monthly production report (all wells)`,
        `- daily-detail-${period}.csv: Daily detail rows for the selected month (per well/day)`,
        `- chlorine-config-snapshot-${period}.csv: Chlorine system configuration snapshot rows for the selected month`,
        '',
      ].join('\n');

      zip.file('README.txt', readmeText);

      // Supervisor stats CSV
      zip.file(`supervisor-stats-${year}-${month.padStart(2, '0')}.csv`, generateSupervisorStatsCSV(stats));

      // All wells production CSV
      zip.file(`all-wells-production-${year}-${month.padStart(2, '0')}.csv`, generateAllWellsCSV(allWellsQuery.data || []));

      // Daily detail CSV
      zip.file(`daily-detail-${year}-${month.padStart(2, '0')}.csv`, generateDailyDetailCSV(dailyDetailQuery.data || []));

      // Chlorine config snapshot CSV
      zip.file(`chlorine-config-snapshot-${year}-${month.padStart(2, '0')}.csv`, generateConfigSnapshotCSV(configSnapshotQuery.data || []));

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `state-report-pack-${year}-${month.padStart(2, '0')}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('State Report Pack ZIP downloaded');
    } catch (error) {
      console.error('ZIP generation failed:', error);
      toast.error('Failed to generate ZIP');
    } finally {
      setGenerating(false);
    }
  };

  // RBAC Guard
  if (user?.role === 'operator') {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="container py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-xl font-bold">Supervisor Stats</h1>
            </div>
          </div>
        </header>
        <main className="container py-6">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Supervisor Access Required</h2>
              <p className="text-muted-foreground mb-6">This page is for supervisors and admins only.</p>
              <Button onClick={() => navigate("/")}>
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const monthName = format(new Date(parseInt(year), parseInt(month) - 1, 1), "MMMM yyyy");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-xl font-bold">Supervisor Monthly Stats</h1>
          </div>
        </div>
      </header>

      <main className="container py-6 max-w-7xl">
        {/* Controls */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Select Month</CardTitle>
            <CardDescription>Monthly aggregates per well (gallons pumped, chlorine usage, quality flags). Weekly Cl lbs: W1=1-7, W2=8-14, W3=15-21, W4=22-28, W5=29-end</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="year">Year</Label>
              <Input
                id="year"
                type="number"
                value={year}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setYear(e.target.value)}
                min="2020"
                max="2050"
              />
            </div>
            <div>
              <Label htmlFor="month">Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger id="month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {format(new Date(2000, i), "MMMM")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleGenerate}>
                <Calendar className="w-4 h-4 mr-2" />
                Load Stats
              </Button>
              <Button 
                onClick={handleStateReportPack} 
                disabled={!stats || generating}
                variant={generating ? "secondary" : "default"}
              >
                {generating ? 'Generating...' : '📦 State Report Pack ZIP'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {stats && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-primary">{stats.systemTotals.totalMG.toFixed(3)} MG</div>
                  <p className="text-sm text-muted-foreground">Total Production</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-primary">{stats.systemTotals.totalLbsCl2?.toFixed(2) || '—'}</div>
                  <p className="text-sm text-muted-foreground">Total Cl₂ (lbs)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold">{stats.wells.length}</div>
                  <p className="text-sm text-muted-foreground">Wells</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-destructive">{stats.systemTotals.missingDaysCount}</div>
                  <p className="text-sm text-muted-foreground">Missing Days</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-destructive">{stats.systemTotals.missingResidualCount}</div>
                  <p className="text-sm text-muted-foreground">Missing Residual</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-destructive">{stats.systemTotals.missingClCalcCount}</div>
                  <p className="text-sm text-muted-foreground">Missing Cl Calc</p>
                </CardContent>
              </Card>
            </div>

            {/* Stats Table */}
            <Card>
              <CardHeader>
                <CardTitle>Per-Well Monthly Stats ({monthName})</CardTitle>
                <CardDescription>Effective values (corrections applied). Period: {stats.period.start} – {stats.period.end}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Well</TableHead>
                      <TableHead className="text-right">Gallons</TableHead>
                      <TableHead className="text-right">MG</TableHead>
                      <TableHead className="text-right">Cl Inches</TableHead>
                      <TableHead className="text-right">Cl Gal</TableHead>
                      <TableHead className="text-right">Cl lbs</TableHead>
                      <TableHead className="text-right">Residual (last / avg)</TableHead>
                      <TableHead className="text-right">Missing Days</TableHead>
                      <TableHead className="text-right">Flags</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.wells.map((well: WellStats) => (
                      <TableRow key={well.wellId}>
                        <TableCell className="font-medium">{well.wellName}</TableCell>
                        <TableCell className="text-right">{well.totalGallons.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{well.totalMG.toFixed(3)}</TableCell>
                        <TableCell className="text-right">{well.totalInchesUsed.toFixed(1)}</TableCell>
                        <TableCell className="text-right">{well.totalGallonsDelivered?.toLocaleString() || '—'}</TableCell>
                        <TableCell className="text-right font-semibold">{well.totalLbsCl2?.toFixed(2) || '—'}</TableCell>
                        <TableCell className="text-right">
                          {well.residualLast ? well.residualLast.toFixed(1) : '—'} / {well.residualAvg ? well.residualAvg.toFixed(1) : '—'}
                        </TableCell>
                        <TableCell className="text-right">{well.missingDaysCount}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-end">
                            <Badge variant="outline" className={well.missingResidualCount > 0 ? "text-destructive" : ""}>
                              R{well.missingResidualCount}
                            </Badge>
                            <Badge variant="outline" className={well.missingClCalcCount > 0 ? "text-destructive" : ""}>
                              C{well.missingClCalcCount}
                            </Badge>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/50 font-bold">
                      <TableCell>System Totals</TableCell>
                      <TableCell className="text-right">{stats.systemTotals.totalGallons.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{stats.systemTotals.totalMG.toFixed(3)}</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">{stats.systemTotals.totalLbsCl2.toFixed(2)}</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">{stats.systemTotals.missingDaysCount}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Badge variant="outline" className={stats.systemTotals.missingResidualCount > 0 ? "text-destructive" : ""}>
                            R{stats.systemTotals.missingResidualCount}
                          </Badge>
                          <Badge variant="outline" className={stats.systemTotals.missingClCalcCount > 0 ? "text-destructive" : ""}>
                            C{stats.systemTotals.missingClCalcCount}
                          </Badge>
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
